import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import {
  fetchAvailableConsentsApi,
  fetchConsentHistoryApi,
  saveConsentApi,
  revokeConsentApi,
  fetchConsentDetailsApi
} from '../api/consentApi'
import {
  syncConsentsFromHistory,
  syncMiniAppsFromHistoryData
} from '../redux/reducers/consentReducer'
import { getUserLanguage } from '../utils/localStorage'

export const useAvailableConsents = (params = {}, options = {}) => {
  const dispatch = useDispatch()
  const lang = getUserLanguage() || 'en'
  const query = useQuery({
    queryKey: ['AVAILABLE_CONSENTS', { lang, ...params }],
    queryFn: async () => {
      const resp = await fetchAvailableConsentsApi(params)
      return resp
    },
    staleTime: 0,
    ...options
  })

  useEffect(() => {
    if (query.isSuccess && query.data?.results && Array.isArray(query.data.results)) {
      const allHistoryItems = query.data.results.flatMap(
        (item) => item.history_data || []
      )
      const hasHistoryField = query.data.results.some((item) => Array.isArray(item.history_data))
      if (allHistoryItems.length > 0 || hasHistoryField) {
        dispatch(syncMiniAppsFromHistoryData(allHistoryItems))
      }
    }
  }, [query.isSuccess, query.data, dispatch])

  return query
}

export const extractHistoryList = (apiResponse) => {
  if (!apiResponse) return []
  if (Array.isArray(apiResponse)) return apiResponse
  if (Array.isArray(apiResponse.data)) return apiResponse.data
  if (Array.isArray(apiResponse.results)) return apiResponse.results
  if (Array.isArray(apiResponse.history)) return apiResponse.history
  return []
}

export const useConsentHistory = (options = {}) => {
  const dispatch = useDispatch()
  const query = useQuery({
    queryKey: ['CONSENT_HISTORY'],
    queryFn: async () => {
      const resp = await fetchConsentHistoryApi()
      return resp
    },
    staleTime: 0,
    ...options
  })

  useEffect(() => {
    if (query.isSuccess && query.data) {
      const items = extractHistoryList(query.data)
      dispatch(syncConsentsFromHistory(items))
    }
  }, [query.isSuccess, query.data, dispatch])

  return query
}

export const useSaveConsent = (options = {}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => saveConsentApi(data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['AVAILABLE_CONSENTS'] })
      queryClient.invalidateQueries({ queryKey: ['CONSENT_HISTORY'] })
      if (options.onSuccess) {
        options.onSuccess(data, variables, context)
      }
    },
    ...options
  })
}

export const useRevokeConsent = (options = {}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => revokeConsentApi(data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['AVAILABLE_CONSENTS'] })
      queryClient.invalidateQueries({ queryKey: ['CONSENT_HISTORY'] })
      if (options.onSuccess) {
        options.onSuccess(data, variables, context)
      }
    },
    ...options
  })
}

export const useConsentDetails = (scope, options = {}) => {
  return useQuery({
    queryKey: ['CONSENT_DETAILS', scope],
    queryFn: async () => {
      const resp = await fetchConsentDetailsApi(scope)
      return resp
    },
    enabled: !!scope,
    staleTime: 5 * 60 * 1000,
    ...options
  })
}
